import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './ManageProduct.css';
import axios from 'axios';

const ManageProduct: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<any>(null);
  const [productName, setProductName] = useState('');
  const [price, setPrice] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<number | null>(null);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/products/${id}`);
        setProduct(res.data);
        setProductName(res.data.name);
        setPrice(res.data.price.toString());
        setLocation(res.data.pickup_location || '');
        setDescription(res.data.description || '');
        setPreviewUrls(res.data.images.map((img: any) => img.image_url));
      } catch (err) {
        setError('Failed to fetch product.');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const newPreviewUrls = filesArray.map(file => URL.createObjectURL(file));
      setImages(prev => [...prev, ...filesArray]);
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  };

  const removeImage = (index: number) => {
    const newImages = [...images];
    const newPreviewUrls = [...previewUrls];
    if (index >= product.images?.length) {
      URL.revokeObjectURL(newPreviewUrls[index]);
      newImages.splice(index - product.images.length, 1);
    }
    newPreviewUrls.splice(index, 1);
    setImages(newImages);
    setPreviewUrls(newPreviewUrls);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productName || !price || !location || !description) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('name', productName);
      formData.append('price', price);
      formData.append('pickup_location', location);
      formData.append('description', description);

      images.forEach((image) => {
        formData.append('images', image); // multiple files under same field name
      });
      

      const deletedImageIds: number[] = [];
      product.images.forEach((img: any, index: number) => {
        if (!previewUrls.includes(img.image_url)) {
          deletedImageIds.push(img.id);
        }
      });

      deletedImageIds.forEach(id => formData.append('delete_images', id.toString()));
      const token = localStorage.getItem("token"); // ✅ Again, read from localStorage
      await axios.put(
        `https://new-thriftshop.onrender.com/api/products/${product.id}`,
        formData, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      

      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      setError('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/products/${product.id}`);
      navigate('/your-products');
    } catch (err) {
      console.error(err);
      setError('Failed to delete product');
    }
  };

  const handleMarkAsSold = async () => {
    try {
      await api.put(`/products/${product.id}`, { is_sold: true });
      navigate('/your-products');
    } catch (err) {
      console.error(err);
      setError('Failed to mark product as sold');
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container"><div className="loading">Loading...</div></div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <div className="container"><div className="not-found">Product not found</div></div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="manage-product-container">
        <div className="container">
          <div className="manage-product-content">
            <h1 className="page-title">MANAGE PRODUCT</h1>
            <div className="manage-actions">
              <button type="button" onClick={() => setShowDeleteConfirm(true)} className="delete-btn">Delete</button>
              <button type="button" onClick={handleMarkAsSold} className="mark-sold-btn">Mark as sold</button>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSave} className="manage-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="productName">Product Name</label>
                  <input type="text" id="productName" value={productName} onChange={e => setProductName(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label htmlFor="price">Price</label>
                  <input type="number" id="price" value={price} onChange={e => setPrice(e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="location">Pick Up location</label>
                <input type="text" id="location" value={location} onChange={e => setLocation(e.target.value)} required />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea id="description" value={description} onChange={e => setDescription(e.target.value)} rows={5} required />
              </div>
              <div className="form-group">
                <label>Upload Pictures</label>
                <div className="upload-container">
                  <div className="upload-area">
                    <p>Browse and choose the files to upload from your computer</p>
                    <input type="file" id="images" onChange={handleImageChange} multiple accept="image/*" className="file-input" />
                    <label htmlFor="images" className="upload-btn"><span className="plus-icon">+</span></label>
                  </div>
                  {previewUrls.length > 0 && (
                    <div className="image-previews">
                      {previewUrls.map((url, index) => (
                        <div key={index} className="image-preview">
                          <img src={url} alt={`Preview ${index}`} />
                          <button type="button" onClick={() => removeImage(index)} className="remove-image-btn">&times;</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="save-btn" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
                <button type="button" onClick={() => navigate('/your-products')} className="cancel-btn">Cancel</button>
              </div>
            </form>
            {showSuccessModal && (
              <div className="modal-overlay">
                <div className="success-modal">
                  <div className="success-icon">✓</div>
                  <h3>Successfully Saved!</h3>
                  <button onClick={() => { setShowSuccessModal(false); navigate('/your-products'); }} className="return-btn">Return</button>
                </div>
              </div>
            )}
            {showDeleteConfirm && (
              <div className="modal-overlay">
                <div className="confirm-modal">
                  <div className="warning-icon">!</div>
                  <h3>Are You Sure You Want to Delete?</h3>
                  <div className="confirm-actions">
                    <button onClick={handleDelete} className="confirm-delete-btn">Delete</button>
                    <button onClick={() => setShowDeleteConfirm(false)} className="cancel-delete-btn">Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageProduct;
